import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import { StatusServerErrors } from '@shared/enums/status-server-errors';

import type { DataTransformer } from '@compositeComponents/hub/personal-view-and-edit/General/Edit/config';
import type {
  DigitalPersonalCardType,
  UpdateDigitalPersonalCardType,
} from '@compositeComponents/hub/personal-view-and-edit/types/personal-types';
import type { BodyArtRequestData } from '@compositeComponents/modals/hubs/profile/BodyArtsModal/components/BodyArtsModalContainer/const/bodyArts-types';
import type { HealthInsuranceRequestType } from '@compositeComponents/modals/hubs/profile/HealthInsuranceModal/HealthInsuranceContainer/utils/healthInsuranceToRequest';
import type { ProfileViewDataEnum } from '@shared/enums/profileViewData.enum';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { MediaType } from '@shared/models/media.model';
import type { ProfileModel } from '@shared/models/profile/profile.model';
import type { HealthInsurance, HealthInsurances } from '@shared/models/profile/profileHealthInsurance.model';
import type {
  AppearanceModelToRequest,
  AttachUserToShareProfile,
  BodyArtModel,
  DraftItemModel,
  DraftStepModel,
  GetBodyArts,
  PersonalDraftStep,
  PersonalModel,
  ProfilePublishResponse,
  ProfileUserSharingModel,
} from '@shared/models/profileNew/profileNew.model';
import type { RootState } from '@store/store';

export const getDigitalPersonalCardByID = createAsyncThunk<
  DigitalPersonalCardType,
  string,
  { rejectValue: ErrorType }
>('getDigitalPersonalCardByID', async (id, { rejectWithValue }) => {
  try {
    return await api.personal.digitalPersonalCardByID(id);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const updateDigitalPersonalCard = createAsyncThunk<
  DigitalPersonalCardType,
  UpdateDigitalPersonalCardType,
  { rejectValue: ErrorType }
>('updateDigitalPersonalCard', async (data, { rejectWithValue }) => {
  try {
    return await api.personal.updateDigitalPersonal(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const getProfileNewInfo = createAsyncThunk<PersonalModel, undefined, { rejectValue: ErrorType }>(
  'getProfileNewInfo',
  async (_, { rejectWithValue }) => {
    try {
      return await api.personal.getProfile();
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const updateProfileNewGeneralInfo = createAsyncThunk<
  PersonalModel,
  DataTransformer,
  { rejectValue: ErrorType }
>('updateProfileNewGeneralInfo', async (props, { rejectWithValue }) => {
  try {
    return await api.personal.updateProfileGeneralInfo(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const disableGeneralInfoPopup = createAsyncThunk<ProfileModel, undefined>(
  'disableProfileNewGeneralInfoPopup',
  async (_, { rejectWithValue }) => {
    try {
      return await api.personal.disableGeneralInfoPopup();
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const getProfileNewDocuments = createAsyncThunk<MediaType[], undefined, { rejectValue: ErrorType }>(
  'getProfileNewDocuments',
  async (_, { rejectWithValue }) => {
    try {
      return await api.personal.getDocuments();
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const updateProfileAppearance = createAsyncThunk<
  AppearanceModelToRequest,
  AppearanceModelToRequest,
  { rejectValue: ErrorType }
>('updateAppearance', async (props, { rejectWithValue }) => {
  try {
    return await api.personal.updateAppearance(props);
  } catch (e: any) {
    const axiosError = e as ServerErrorType;

    return rejectWithValue(axiosError.data);
  }
});

export const getBodyArts = createAsyncThunk<
  GetBodyArts,
  undefined,
  { rejectValue: ErrorType; state: RootState }
>('getBodyArts', async (_, { rejectWithValue, getState }) => {
  try {
    const page = getState().personal.bodyArtsMeta.page;

    return await api.personal.getBodyArts(page);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const createBodyArt = createAsyncThunk<BodyArtModel, BodyArtRequestData, { rejectValue: ErrorType }>(
  'createBodyArt',
  async (props, { rejectWithValue }) => {
    try {
      return await api.personal.createProfileBodyArt(props);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const updateBodyArt = createAsyncThunk<
  BodyArtModel,
  { id: number } & BodyArtRequestData,
  { rejectValue: ErrorType }
>('updateBodyArt', async (props, { rejectWithValue }) => {
  try {
    return await api.personal.updateProfileBodyArt(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const deleteBodyArt = createAsyncThunk<number, { id: number }, { rejectValue: ErrorType }>(
  'deleteBodyArt',
  async (props, { rejectWithValue }) => {
    try {
      return await api.personal.deleteProfileBodyArt(props).then(() => props.id);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const getProfileSharingUsers = createAsyncThunk<
  Array<ProfileUserSharingModel>,
  undefined,
  { rejectValue: ErrorType }
>('getProfileSharingUsers', async (_, { rejectWithValue }) => {
  try {
    return await api.personal.getSharedUsers();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const attachUserToShareProfile = createAsyncThunk<
  ProfileUserSharingModel,
  AttachUserToShareProfile,
  { rejectValue: ErrorType }
>('attachUserToShareProfile', async (props, { rejectWithValue }) => {
  try {
    return await api.personal.attachUserToShare(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const detachUserFromShareProfile = createAsyncThunk<
  number,
  { userId: number },
  { rejectValue: ErrorType }
>('detachUserFromShareProfile', async (props, { rejectWithValue }) => {
  try {
    return await api.personal.detachUserFromShare(props.userId).then(() => props.userId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateUserShareProfilePermissions = createAsyncThunk<
  ProfileUserSharingModel,
  AttachUserToShareProfile,
  { rejectValue: ErrorType }
>('updateUserShareProfilePermissions', async (props, { rejectWithValue }) => {
  try {
    return await api.personal.updateUserSharePermissions(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const disableShareInfoPopup = createAsyncThunk<ProfileModel, undefined>(
  'disableProfileShareInfoPopup',
  async (_, { rejectWithValue }) => {
    try {
      return await api.personal.disableShareInfoPopup();
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const getPersonalDraftData = createAsyncThunk<DraftItemModel, undefined, { rejectValue: ErrorType }>(
  'getPersonalDraftData',
  async (_, { rejectWithValue }) => {
    try {
      return await api.personal.getPersonalDraft();
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const savePersonalDraftData = createAsyncThunk<
  DraftItemModel & {},
  DraftItemModel & { insurance_reminder?: { is_all_day: boolean; due_dated_at: string } },
  { rejectValue: ErrorType }
>('savePersonalDraftData', async (data, { rejectWithValue }) => {
  try {
    return await api.personal.savePersonalDraft(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getPersonalDraftStep = createAsyncThunk<
  Array<DraftStepModel>,
  undefined,
  { rejectValue: ErrorType }
>('getPersonalDraftStep', async (_, { rejectWithValue }) => {
  try {
    return await api.personal.getPersonalDraftStep();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const savePersonalDraftStep = createAsyncThunk<
  DraftStepModel,
  PersonalDraftStep,
  { rejectValue: ErrorType }
>('savePersonalDraftStep', async (data, { rejectWithValue }) => {
  try {
    return await api.personal.setPersonalDraftStep(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const updateViewDataKey = createAsyncThunk<ProfileModel, ProfileViewDataEnum>(
  'disableProfileNewGeneralInfoPopup',
  async (data, { rejectWithValue }) => {
    try {
      return await api.personal.updateViewDataKey(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const publishPersonalData = createAsyncThunk<ProfilePublishResponse, undefined>(
  'publishPersonalData',
  async (_, { rejectWithValue }) => {
    try {
      return await api.personal.publishPersonalData();
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

// ---------------- Health Insurance ----------------
export const getHealthInsurances = createAsyncThunk<
  HealthInsurances[],
  undefined,
  { rejectValue: ErrorType }
>('getHealthInsurances', async (_, { rejectWithValue }) => {
  try {
    return await api.personal.getHealthInsurances();
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getHealthInsurance = createAsyncThunk<HealthInsurance, number, { rejectValue: ErrorType }>(
  'getHealthInsurance',
  async (insuranceID, { rejectWithValue }) => {
    try {
      return await api.personal.getHealthInsurance(insuranceID);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const createHealthInsurance = createAsyncThunk<
  HealthInsurance,
  { entity_id: number } & HealthInsuranceRequestType,
  { rejectValue: ErrorType }
>('createHealthInsurance', async (props, { rejectWithValue }) => {
  try {
    return await api.personal.createHealthInsurance(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });

    return rejectWithValue(axiosError.data);
  }
});

export const updateHealthInsurance = createAsyncThunk<
  HealthInsurance,
  { id: number; entity_id: number } & Partial<HealthInsuranceRequestType>,
  { rejectValue: ErrorType }
>('updateHealthInsurance', async (props, { rejectWithValue }) => {
  try {
    return await api.personal.updateHealthInsurance(props);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });

    return rejectWithValue(axiosError.data);
  }
});

export const deleteHealthInsurance = createAsyncThunk<
  number,
  { insuranceID: number; entity_id: number },
  { rejectValue: ErrorType }
>('deleteHealthInsurance', async (props, { rejectWithValue }) => {
  try {
    return await api.personal.deleteHealthInsurance(props).then(() => props.insuranceID);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
